import { useEffect, useRef, useState } from 'react';

import { checkAlertSlug, suggestAlertSlug } from 'api/alert-slug';
import { news_alerts } from 'lang/en';
import styles from 'styles/modules/newsAlert/SlugField.module.scss';

const DEBOUNCE_MS = 400;

// Admin-only: the address the alert lives at. On create it follows the subject
// line (short, stop words dropped) until the admin types their own. Whatever is
// in the field is checked against every current and retired alert slug, so a
// taken address shows up before the save does. `onStatus` tells the parent
// whether the value can be submitted.
const SlugField = ({
    value,
    onChange,
    onStatus,
    title,
    alertId,
    suggestFromTitle = false,
    currentSlug = null,
}) => {
    const copy = news_alerts.create_alert.screen_1;
    const [touched, setTouched] = useState(false);
    const [check, setCheck] = useState(null);
    const [checking, setChecking] = useState(false);
    const latest = useRef(0);

    const unchanged = Boolean(currentSlug) && value === currentSlug;

    useEffect(() => {
        if (!suggestFromTitle || touched || !title?.trim()) {
            return undefined;
        }
        // A reply that lands after the admin started typing, or after the
        // title changed again, must not overwrite the field.
        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                const { slug } = await suggestAlertSlug(title);
                if (!cancelled) onChange(slug);
            } catch (err) {
                console.warn('slug suggestion failed', err);
            }
        }, DEBOUNCE_MS);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [title, touched, suggestFromTitle]);

    useEffect(() => {
        // Every change invalidates whatever check is in flight, including a
        // change back to empty or to the current address, so a late reply can
        // never flag the field as taken.
        const seq = ++latest.current;
        setCheck(null);
        if (!value?.trim() || unchanged) {
            setChecking(false);
            return undefined;
        }
        setChecking(true);
        const timer = setTimeout(async () => {
            try {
                const result = await checkAlertSlug(value, alertId);
                if (seq === latest.current) setCheck(result);
            } catch (err) {
                console.warn('slug check failed', err);
                if (seq === latest.current) setCheck(null);
            } finally {
                if (seq === latest.current) setChecking(false);
            }
        }, DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [value, alertId, unchanged]);

    useEffect(() => {
        if (!onStatus) return;
        // Submittable unless we know the address is taken or it is unusable.
        const unusable = Boolean(value?.trim()) && check && !check.slug;
        onStatus({
            ok: !checking && !(check && check.available === false) && !unusable,
            checking,
        });
    }, [check, checking, value]);

    let status = null;
    let statusClass = '';
    if (unchanged) {
        status = copy.slug_current;
    } else if (checking) {
        status = copy.slug_checking;
    } else if (check && !check.slug) {
        status = copy.slug_unusable;
        statusClass = styles.taken;
    } else if (check && check.available === false) {
        status = copy.slug_taken;
        statusClass = styles.taken;
    } else if (check) {
        status =
            check.slug !== value
                ? `${copy.slug_available} /news-alerts/${check.slug}`
                : copy.slug_available;
        statusClass = styles.available;
    } else if (!value?.trim()) {
        status = copy.slug_hint;
    }

    return (
        <div className={styles.wrapper}>
            <label className={styles.label} htmlFor="alert-slug">
                {copy.slug_label}
            </label>
            <div className={styles.row}>
                <span className={styles.prefix}>/news-alerts/</span>
                <input
                    id="alert-slug"
                    name="slug"
                    type="text"
                    className={styles.input}
                    value={value || ''}
                    autoComplete="off"
                    spellCheck={false}
                    onChange={(event) => {
                        setTouched(true);
                        onChange(event.target.value);
                    }}
                />
            </div>
            {status && (
                <p className={[styles.status, statusClass].join(' ')}>
                    {status}
                </p>
            )}
        </div>
    );
};

export default SlugField;
